import { Injectable, OnDestroy } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject, timer, interval } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface SignalRMessage {
  type: string;
  data: any;
  timestamp: Date;
  user?: string;
  connectionId?: string;
}

export interface ScanningProgressUpdate {
  preAlertId: string;
  totalCartons: number;
  scannedCartons: number;
  completionPercentage: number;
  scanningVelocity: number;
  estimatedCompletion: Date;
  lastScanTime: Date;
  activeScanner?: string;
}

export interface OrderStatusUpdate {
  orderId: string;
  previousStatus: string;
  newStatus: string;
  triggeredBy: 'scanning' | 'manual' | 'system';
  scanCount: number;
  completionPercentage: number;
  statusChangeTime: Date;
  preAlertStatuses: any[];
}

export interface ScanActivityBroadcast {
  scanId: string;
  barcode: string;
  preAlertId: string;
  orderId?: string;
  scannerUserId: string;
  scannerUserName: string;
  scanTime: Date;
  success: boolean;
  statusChanges: string[];
}

export interface CollaborativeScanUpdate {
  sessionId: string;
  preAlertId: string;
  activeUsers: {
    userId: string;
    userName: string;
    lastActivity: Date;
    scanCount: number;
  }[];
  totalProgress: number;
  distributedProgress: {
    userId: string;
    assignedCartons: number;
    completedCartons: number;
  }[];
}

export interface ConnectionStatus {
  state: HubConnectionState;
  isConnected: boolean;
  connectionId?: string;
  lastConnected?: Date;
  lastDisconnected?: Date;
  reconnectAttempts: number;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService implements OnDestroy {
  private connection: HubConnection | null = null;
  private destroy$ = new Subject<void>();
  private reconnectTimer$ = new Subject<void>();
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000; // 5 seconds
  private currentReconnectAttempts = 0;
  
  // Observables for component subscriptions
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>({
    state: HubConnectionState.Disconnected,
    isConnected: false,
    reconnectAttempts: 0
  });
  
  private messagesSubject = new Subject<SignalRMessage>();
  private notificationsSubject = new Subject<SignalRMessage>();
  private orderUpdatesSubject = new Subject<SignalRMessage>();
  private systemAlertsSubject = new Subject<SignalRMessage>();

  // Scanning-specific subjects
  private scanningProgressSubject = new Subject<ScanningProgressUpdate>();
  private orderStatusUpdatesSubject = new Subject<OrderStatusUpdate>();
  private scanActivitySubject = new Subject<ScanActivityBroadcast>();
  private collaborativeScanSubject = new Subject<CollaborativeScanUpdate>();

  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  public messages$ = this.messagesSubject.asObservable();
  public notifications$ = this.notificationsSubject.asObservable();
  public orderUpdates$ = this.orderUpdatesSubject.asObservable();
  public systemAlerts$ = this.systemAlertsSubject.asObservable();

  // Scanning-specific observables
  public scanningProgress$ = this.scanningProgressSubject.asObservable();
  public orderStatusUpdates$ = this.orderStatusUpdatesSubject.asObservable();
  public scanActivity$ = this.scanActivitySubject.asObservable();
  public collaborativeScanning$ = this.collaborativeScanSubject.asObservable();

  constructor(private authService: AuthService) {
    // Auto-connect when user is authenticated
    this.authService.isLoggedIn$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isAuthenticated => {
      if (isAuthenticated && !this.isConnected) {
        this.connect();
      } else if (!isAuthenticated && this.isConnected) {
        this.disconnect();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reconnectTimer$.next();
    this.reconnectTimer$.complete();
    this.disconnect();
  }

  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  get connectionId(): string | undefined {
    return this.connection?.connectionId || undefined;
  }

  async connect(): Promise<void> {
    if (this.connection && this.isConnected) {
      return;
    }

    try {
      // Get auth token
      const token = await this.authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Build SignalR connection
      this.connection = new HubConnectionBuilder()
        .withUrl(`${environment.apiUrl}/hub/notifications`, {
          accessTokenFactory: () => token
        })
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .configureLogging(LogLevel.Information)
        .build();

      // Setup event handlers
      this.setupEventHandlers();

      // Start connection
      await this.connection.start();
      
      this.currentReconnectAttempts = 0;
      this.updateConnectionStatus();

      console.log('SignalR connected successfully');

    } catch (error) {
      console.error('Failed to connect to SignalR:', error);
      this.updateConnectionStatus(error instanceof Error ? error.message : 'Connection failed');
      this.scheduleReconnect();
    }
  }

  async disconnect(): Promise<void> {
    this.reconnectTimer$.next(); // Stop reconnection attempts
    
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('Error disconnecting from SignalR:', error);
      } finally {
        this.connection = null;
        this.updateConnectionStatus();
      }
    }
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Connection state events
    this.connection.onclose((error) => {
      console.log('SignalR connection closed', error);
      this.updateConnectionStatus(error?.message);
      this.scheduleReconnect();
    });

    this.connection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
      this.updateConnectionStatus(error?.message);
    });

    this.connection.onreconnected((connectionId) => {
      console.log('SignalR reconnected', connectionId);
      this.currentReconnectAttempts = 0;
      this.updateConnectionStatus();
    });

    // Message handlers
    this.connection.on('ReceiveNotification', (type: string, data: any, user?: string) => {
      const message: SignalRMessage = {
        type: 'notification',
        data: { type, ...data },
        timestamp: new Date(),
        user,
        connectionId: this.connectionId
      };
      
      this.messagesSubject.next(message);
      this.notificationsSubject.next(message);
    });

    this.connection.on('OrderUpdate', (orderData: any, user?: string) => {
      const message: SignalRMessage = {
        type: 'orderUpdate',
        data: orderData,
        timestamp: new Date(),
        user,
        connectionId: this.connectionId
      };
      
      this.messagesSubject.next(message);
      this.orderUpdatesSubject.next(message);
    });

    this.connection.on('SystemAlert', (alertData: any, user?: string) => {
      const message: SignalRMessage = {
        type: 'systemAlert',
        data: alertData,
        timestamp: new Date(),
        user,
        connectionId: this.connectionId
      };
      
      this.messagesSubject.next(message);
      this.systemAlertsSubject.next(message);
    });

    this.connection.on('PreAlertUpdate', (preAlertData: any, user?: string) => {
      const message: SignalRMessage = {
        type: 'preAlertUpdate',
        data: preAlertData,
        timestamp: new Date(),
        user,
        connectionId: this.connectionId
      };
      
      this.messagesSubject.next(message);
      this.notificationsSubject.next(message);
    });

    this.connection.on('DocumentStatusUpdate', (documentData: any, user?: string) => {
      const message: SignalRMessage = {
        type: 'documentUpdate',
        data: documentData,
        timestamp: new Date(),
        user,
        connectionId: this.connectionId
      };
      
      this.messagesSubject.next(message);
      this.notificationsSubject.next(message);
    });

    // Scanning progress events
    this.connection.on('ScanningProgressUpdate', (progressData: ScanningProgressUpdate) => {
      this.scanningProgressSubject.next(progressData);

      const message: SignalRMessage = {
        type: 'scanningProgress',
        data: progressData,
        timestamp: new Date(),
        connectionId: this.connectionId
      };
      this.messagesSubject.next(message);
    });

    this.connection.on('OrderStatusUpdate', (statusData: OrderStatusUpdate) => {
      this.orderStatusUpdatesSubject.next(statusData);

      const message: SignalRMessage = {
        type: 'orderStatusUpdate',
        data: statusData,
        timestamp: new Date(),
        connectionId: this.connectionId
      };
      this.messagesSubject.next(message);
    });

    this.connection.on('ScanActivity', (scanData: ScanActivityBroadcast) => {
      this.scanActivitySubject.next(scanData);

      const message: SignalRMessage = {
        type: 'scanActivity',
        data: scanData,
        timestamp: new Date(),
        connectionId: this.connectionId
      };
      this.messagesSubject.next(message);
    });

    this.connection.on('CollaborativeScanUpdate', (collaborativeData: CollaborativeScanUpdate) => {
      this.collaborativeScanSubject.next(collaborativeData);

      const message: SignalRMessage = {
        type: 'collaborativeScanning',
        data: collaborativeData,
        timestamp: new Date(),
        connectionId: this.connectionId
      };
      this.messagesSubject.next(message);
    });

    // Generic message handler
    this.connection.on('ReceiveMessage', (type: string, data: any, user?: string) => {
      const message: SignalRMessage = {
        type,
        data,
        timestamp: new Date(),
        user,
        connectionId: this.connectionId
      };

      this.messagesSubject.next(message);
    });
  }

  private updateConnectionStatus(error?: string): void {
    const status: ConnectionStatus = {
      state: this.connection?.state || HubConnectionState.Disconnected,
      isConnected: this.isConnected,
      connectionId: this.connectionId,
      reconnectAttempts: this.currentReconnectAttempts,
      error
    };

    if (this.isConnected) {
      status.lastConnected = new Date();
      status.error = undefined;
    } else {
      status.lastDisconnected = new Date();
    }

    this.connectionStatusSubject.next(status);
  }

  private scheduleReconnect(): void {
    if (this.currentReconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      return;
    }

    this.currentReconnectAttempts++;
    
    timer(this.reconnectInterval)
      .pipe(takeUntil(this.reconnectTimer$))
      .subscribe(() => {
        console.log(`Attempting reconnection ${this.currentReconnectAttempts}/${this.maxReconnectAttempts}`);
        this.connect();
      });
  }

  // Public methods for sending messages
  async sendMessage(method: string, ...args: any[]): Promise<void> {
    if (!this.isConnected || !this.connection) {
      throw new Error('SignalR connection is not established');
    }

    try {
      await this.connection.invoke(method, ...args);
    } catch (error) {
      console.error('Failed to send SignalR message:', error);
      throw error;
    }
  }

  async joinGroup(groupName: string): Promise<void> {
    return this.sendMessage('JoinGroup', groupName);
  }

  async leaveGroup(groupName: string): Promise<void> {
    return this.sendMessage('LeaveGroup', groupName);
  }

  async sendNotification(type: string, data: any, targetUser?: string): Promise<void> {
    return this.sendMessage('SendNotification', type, data, targetUser);
  }

  async sendOrderUpdate(orderData: any, targetGroup?: string): Promise<void> {
    return this.sendMessage('SendOrderUpdate', orderData, targetGroup);
  }

  async sendSystemAlert(alertData: any): Promise<void> {
    return this.sendMessage('SendSystemAlert', alertData);
  }

  // Utility methods
  forceReconnect(): void {
    this.currentReconnectAttempts = 0;
    this.disconnect().then(() => {
      setTimeout(() => this.connect(), 1000);
    });
  }

  getConnectionInfo(): ConnectionStatus {
    return this.connectionStatusSubject.value;
  }

  // Message filtering helpers
  getMessagesByType(messageType: string): Observable<SignalRMessage> {
    return this.messages$.pipe(
      takeUntil(this.destroy$)
    );
  }

  // Connection health check
  startHealthCheck(): void {
    interval(30000) // Check every 30 seconds
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isConnected) {
          console.log('Health check: Connection lost, attempting reconnect');
          this.connect();
        }
      });
  }

  stopHealthCheck(): void {
    // Health check will stop automatically when destroy$ is triggered
  }

  // Get connection statistics
  getConnectionStats(): any {
    const status = this.connectionStatusSubject.value;
    return {
      isConnected: status.isConnected,
      state: HubConnectionState[status.state],
      connectionId: status.connectionId,
      lastConnected: status.lastConnected,
      lastDisconnected: status.lastDisconnected,
      reconnectAttempts: status.reconnectAttempts,
      totalMessages: 0, // Could be tracked if needed
      uptime: status.lastConnected ? new Date().getTime() - status.lastConnected.getTime() : 0
    };
  }

  // Scanning-specific methods
  subscribeToScanningProgress(preAlertId: string): Observable<ScanningProgressUpdate> {
    // Join group for specific pre-alert
    this.joinGroup(`PreAlert_${preAlertId}`).catch(error =>
      console.error('Failed to join pre-alert group:', error)
    );

    return this.scanningProgress$.pipe(
      takeUntil(this.destroy$)
    );
  }

  subscribeToOrderStatusUpdates(orderId: string): Observable<OrderStatusUpdate> {
    // Join group for specific order
    this.joinGroup(`Order_${orderId}`).catch(error =>
      console.error('Failed to join order group:', error)
    );

    return this.orderStatusUpdates$.pipe(
      takeUntil(this.destroy$)
    );
  }

  async broadcastScanActivity(scanData: ScanActivityBroadcast): Promise<void> {
    return this.sendMessage('BroadcastScanActivity', scanData);
  }

  subscribeToCollaborativeScanning(sessionId: string): Observable<CollaborativeScanUpdate> {
    // Join collaborative scanning session
    this.joinGroup(`ScanSession_${sessionId}`).catch(error =>
      console.error('Failed to join scan session group:', error)
    );

    return this.collaborativeScanning$.pipe(
      takeUntil(this.destroy$)
    );
  }

  async notifyOtherScannersOfProgress(progress: {
    preAlertId: string;
    scanCount: number;
    userId: string;
    userName: string;
  }): Promise<void> {
    return this.sendMessage('NotifyScanningProgress', progress);
  }

  async joinScanningSession(preAlertId: string, userId: string, userName: string): Promise<void> {
    return this.sendMessage('JoinScanningSession', preAlertId, userId, userName);
  }

  async leaveScanningSession(preAlertId: string, userId: string): Promise<void> {
    return this.sendMessage('LeaveScanningSession', preAlertId, userId);
  }

  // Subscribe to all scanning events for dashboard views
  subscribeToAllScanningEvents(): Observable<ScanActivityBroadcast> {
    this.joinGroup('AllScanners').catch(error =>
      console.error('Failed to join all scanners group:', error)
    );

    return this.scanActivity$.pipe(
      takeUntil(this.destroy$)
    );
  }

  // Subscribe to real-time order status changes
  subscribeToAllOrderStatusUpdates(): Observable<OrderStatusUpdate> {
    this.joinGroup('OrderStatusUpdates').catch(error =>
      console.error('Failed to join order status updates group:', error)
    );

    return this.orderStatusUpdates$.pipe(
      takeUntil(this.destroy$)
    );
  }

  // Cleanup scanning subscriptions
  async unsubscribeFromScanningEvents(preAlertId?: string, orderId?: string): Promise<void> {
    const promises: Promise<void>[] = [];

    if (preAlertId) {
      promises.push(this.leaveGroup(`PreAlert_${preAlertId}`));
    }

    if (orderId) {
      promises.push(this.leaveGroup(`Order_${orderId}`));
    }

    // Always leave general groups
    promises.push(this.leaveGroup('AllScanners'));
    promises.push(this.leaveGroup('OrderStatusUpdates'));

    await Promise.all(promises);
  }
}